// 정책 관리 패널 — 정책 선택/편집/저장/초기화/추가/삭제 (파일 정책 + 정책별 검사 옵션 + Webhook 시크릿)
import { useEffect, useState } from 'react';
import { useUI } from '../lib/uiStore';
import { usePolicyStore } from '../lib/policyStore';
import { useTab } from '../lib/tabStore';
import { audit } from '../lib/auth';
import { AdminApiError } from '../lib/adminApi';
import { TagInput } from '../components/TagInput';
import { ToggleRow } from '../components/ToggleRow';

export function Policies() {
  const { showToast, openModal } = useUI();
  const setTab = useTab((s) => s.setTab);
  const {
    policies, settings, selectedId,
    selectPolicy, updatePolicy,
    addPolicy, deletePolicy, save, resetToDefaults,
    regenerateWebhookSecret, ensureWebhookSecret,
    extToString, stringToExt, dirty, loadFromServer,
  } = usePolicyStore();

  useEffect(() => { ensureWebhookSecret(); }, [ensureWebhookSecret]);
  useEffect(() => { void loadFromServer(); }, [loadFromServer]);

  const [addOpen, setAddOpen] = useState(false);
  const [newId, setNewId] = useState('');
  const VALID_ID = /^[a-z0-9][a-z0-9_-]*$/i;
  const current = policies[selectedId];
  const policyCount = Object.keys(policies).length;

  function onDelete() {
    if (policyCount <= 1) return;
    openModal({
      title: '🗑 정책 삭제',
      sub: `<strong>${selectedId}</strong> 정책을 삭제합니다. 진행하시겠습니까?`,
      body: '<div>삭제된 정책 ID는 향후 신규 생성 시 재사용 가능합니다.</div>',
      confirmLabel: '확인',
      onConfirm: () => {
        try {
          const id = selectedId;
          deletePolicy(id);
          audit('admin.action', `policy.delete id=${id}`);
          showToast(`${id} 정책이 삭제되었습니다`, 'success');
        } catch (e) {
          showToast(String((e as Error).message), 'error');
        }
      },
    });
  }

  function onReset() {
    openModal({
      title: '⚠ 정책 초기화',
      sub: '모든 정책과 설정이 <strong>기본값으로 되돌아갑니다</strong>.',
      body: '<div>현재 저장된 정책 변경 사항이 모두 삭제됩니다.</div>',
      confirmLabel: '확인',
      onConfirm: () => {
        resetToDefaults();
        audit('admin.action', 'policy.reset_all');
        showToast('초기화되었습니다', 'success');
      },
    });
  }

  // 서버 저장 실패(400 등) 시 서버 message 를 그대로 표시하고 dirty 유지
  async function onSave() {
    try {
      await save();
      audit('admin.action', 'policy.save');
      showToast('정책이 저장되었습니다', 'success');
    } catch (e) {
      showToast(e instanceof AdminApiError ? e.message : '정책 저장 실패 — 백엔드 연결을 확인하세요', 'error');
    }
  }

  function onAdd() {
    try {
      addPolicy(newId, {
        name: `${newId} 정책`,
        maxSizeMB: 100,
        maxFiles: 5,
        allowedExt: ['.pdf'],
        blockedExt: ['.exe', '.bat'],
        engines: ['clamav', 'yara'],
      });
      audit('admin.action', `policy.add id=${newId}`);
      setAddOpen(false);
      const addedId = newId;
      setNewId('');
      showToast(`${addedId} 정책이 추가되었습니다`, 'success');
    } catch (e) {
      showToast(String((e as Error).message), 'error');
    }
  }

  return (
    <>
      <div className="sect-head">
        <div className="sect-title">
          ⚙ 정책 관리 <em>프로젝트별 검사 규칙</em>
          {dirty && (
            <span
              style={{
                marginLeft: 10,
                fontSize: 12,
                color: 'var(--yellow)',
                background: 'var(--yellow-dim)',
                padding: '2px 8px',
                borderRadius: 4,
                fontFamily: 'JetBrains Mono, monospace',
              }}
            >
              변경됨 · 저장 필요
            </span>
          )}
        </div>
        <button className="btn btn-pri btn-sm" onClick={() => setAddOpen(true)}>+ 새 정책 추가</button>
      </div>

      <div
        data-testid="policy-selector"
        style={{
          display: 'flex',
          gap: 8,
          marginBottom: 16,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        {Object.entries(policies).map(([id, p]) => (
          <button
            key={id}
            type="button"
            className={`btn btn-sm ${id === selectedId ? 'btn-pri' : 'btn-ghost'}`}
            onClick={() => selectPolicy(id)}
          >
            {p.name} <span className="text-mono" style={{ opacity: 0.7, marginLeft: 4 }}>({id})</span>
          </button>
        ))}
        <button
          type="button"
          className="btn btn-danger btn-sm"
          onClick={onDelete}
          disabled={policyCount <= 1}
          style={{ marginLeft: 'auto', opacity: policyCount <= 1 ? 0.4 : 1, cursor: policyCount <= 1 ? 'not-allowed' : 'pointer' }}
        >
          🗑 선택 정책 삭제
        </button>
      </div>

      <div className="settings">
        <div className="set-card">
          <div className="set-title">📁 파일 정책 ({selectedId})</div>
          <div className="form-row">
            <label className="form-label" htmlFor="policy-name">정책 이름</label>
            <input
              id="policy-name"
              className="form-input"
              value={current?.name ?? ''}
              onChange={(e) => updatePolicy(selectedId, { name: e.target.value })}
            />
          </div>
          <div className="form-row">
            <label className="form-label" htmlFor="policy-id">정책 ID</label>
            <input id="policy-id" className="form-input mono" value={selectedId} readOnly />
          </div>
          <div className="form-row">
            <label className="form-label" htmlFor="max-size">최대 파일 크기 (MB)</label>
            <input
              id="max-size"
              className="form-input"
              type="number"
              min={1}
              value={current?.maxSizeMB ?? 0}
              onChange={(e) => updatePolicy(selectedId, { maxSizeMB: Number(e.target.value) || 0 })}
            />
          </div>
          <div className="form-row">
            <label className="form-label" htmlFor="max-files">다중 업로드 최대 개수</label>
            <input
              id="max-files"
              className="form-input"
              type="number"
              min={1}
              value={current?.maxFiles ?? 0}
              onChange={(e) => updatePolicy(selectedId, { maxFiles: Number(e.target.value) || 0 })}
            />
          </div>
          <div className="form-row">
            <label className="form-label" htmlFor="allowed-ext">허용 확장자 <span className="text-muted" style={{ fontSize: 12 }}>(입력 후 Enter 또는 쉼표, ✕로 삭제)</span></label>
            <TagInput
              id="allowed-ext"
              tags={current?.allowedExt ?? []}
              onChange={(next) => updatePolicy(selectedId, { allowedExt: next })}
              placeholder=".pdf .docx .jpg …"
            />
          </div>
          <div className="form-row">
            <label className="form-label" htmlFor="blocked-ext">차단 확장자 <span className="text-muted" style={{ fontSize: 12 }}>(입력 후 Enter 또는 쉼표, ✕로 삭제)</span></label>
            <TagInput
              id="blocked-ext"
              tags={current?.blockedExt ?? []}
              onChange={(next) => updatePolicy(selectedId, { blockedExt: next })}
              placeholder=".exe .bat .sh …"
            />
          </div>
          <div className="form-row">
            <label className="form-label" htmlFor="policy-scan-timeout">검사 타임아웃 (초)</label>
            <input
              id="policy-scan-timeout"
              className="form-input"
              type="number"
              min={1}
              value={current?.scanTimeoutS ?? 30}
              onChange={(e) => updatePolicy(selectedId, { scanTimeoutS: Math.max(1, Math.trunc(Number(e.target.value) || 1)) })}
            />
          </div>
          <ToggleRow
            label="Office 매크로 자동 격리 (이 정책)"
            on={current?.quarantineMacro ?? true}
            onClick={() => updatePolicy(selectedId, { quarantineMacro: !(current?.quarantineMacro ?? true) })}
          />
          <ToggleRow
            label="PDF JS 임베드 자동 격리 (이 정책)"
            on={current?.quarantinePdfJs ?? true}
            onClick={() => updatePolicy(selectedId, { quarantinePdfJs: !(current?.quarantinePdfJs ?? true) })}
          />
        </div>

        <div className="set-card">
          <div className="set-title">🔔 Webhook 설정</div>
          <div className="form-row">
            <label className="form-label" htmlFor="wh-secret">Webhook 서명 시크릿 (HMAC-SHA256, 자동 생성)</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                id="wh-secret"
                className="form-input mono"
                type="password"
                value={settings.webhookSecret}
                readOnly
                style={{ flex: 1 }}
              />
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={async () => {
                  try {
                    if (navigator.clipboard?.writeText) {
                      await navigator.clipboard.writeText(settings.webhookSecret);
                      showToast('시크릿이 클립보드에 복사되었습니다', 'success');
                    } else {
                      showToast('이 브라우저에서는 복사 지원 안 됨', 'error');
                    }
                  } catch {
                    showToast('복사 실패', 'error');
                  }
                }}
              >
                📋 복사
              </button>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => {
                  openModal({
                    title: '🔄 Webhook 시크릿 재생성',
                    sub: '기존 시크릿이 무효화됩니다. 호출 프로젝트의 검증 코드를 새 시크릿으로 갱신해야 합니다.',
                    body: '<div>재생성 후에는 반드시 저장하여 적용하세요.</div>',
                    confirmLabel: '확인',
                    onConfirm: () => {
                      regenerateWebhookSecret();
                      showToast('새 시크릿이 생성되었습니다 — 저장 필요', 'warn');
                    },
                  });
                }}
              >
                🔄 재생성
              </button>
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 18, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button className="btn btn-ghost" onClick={onReset}>초기화</button>
        <button
          className="btn btn-pri"
          onClick={() => void onSave()}
        >
          💾 정책 저장
        </button>
      </div>

      <div className="card mt-20" style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ color: 'var(--text2)', lineHeight: 1.8, flex: 1 }}>
          전역 검사 엔진 · 격리 보관 · Webhook 재시도 설정은 🛠 시스템 설정 탭으로 이동했습니다.
        </div>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setTab('settings')}>시스템 설정 열기</button>
      </div>

      {addOpen && (
        <div
          className="modal-bg open"
          onClick={(e) => { if (e.target === e.currentTarget) setAddOpen(false); }}
        >
          <div className="modal">
            <div className="modal-title">+ 새 정책 추가</div>
            <div className="modal-sub">새 정책의 ID는 영문/숫자/하이픈/언더바만 사용 가능합니다.</div>
            <div className="form-row">
              <label className="form-label" htmlFor="new-policy-id">정책 ID</label>
              <input
                id="new-policy-id"
                className="form-input mono"
                placeholder="정책 ID (예: project-a)"
                value={newId}
                onChange={(e) => setNewId(e.target.value)}
                autoFocus
              />
            </div>
            <div className="modal-foot">
              <button className="btn btn-ghost" onClick={() => { setAddOpen(false); setNewId(''); }}>취소</button>
              <button
                className="btn btn-pri"
                onClick={onAdd}
                disabled={!VALID_ID.test(newId)}
                style={{ opacity: !VALID_ID.test(newId) ? 0.4 : 1, cursor: !VALID_ID.test(newId) ? 'not-allowed' : 'pointer' }}
              >
                추가
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
